import { useEffect, useMemo, useState } from "react";
import { GripVertical, Lock, SlidersHorizontal } from "lucide-react";
import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type { AvailableColumn } from "@/api/types";

interface ColumnSelectorProps {
  available: AvailableColumn[];
  activeKeys: string[];
  onChange: (next: string[]) => void;
  onReset: () => void;
  /** Show a small indigo dot on the trigger when the active set differs
   *  from the baseline (saved view's columns or backend defaults). */
  isCustomized?: boolean;
  disabled?: boolean;
  /** Trigger button label. Defaults to "Columns" for ContactsPage +
   *  any other caller that still uses the classic naming. F6
   *  CompaniesPage passes "View settings" to reflect the broader
   *  scope of the dropdown's role in the new layout. */
  triggerLabel?: string;
}

/**
 * Popover-based column manager. Two drag-reorderable groups:
 * - Active (checked) columns at the top, in display order.
 * - Inactive (unchecked) columns at the bottom, also drag-reorderable
 *   so users can prepare a desired order before toggling them on.
 *
 * Locked columns get a 🔒 + disabled-but-checked checkbox; their drag
 * handle stays active so users can still reposition them in the row
 * order. Toggling and reordering both fire onChange immediately.
 */
export function ColumnSelector({
  available,
  activeKeys,
  onChange,
  onReset,
  isCustomized = false,
  disabled = false,
  triggerLabel = "Columns",
}: ColumnSelectorProps) {
  const [open, setOpen] = useState(false);

  const byKey = useMemo(() => {
    const out = new Map<string, AvailableColumn>();
    for (const c of available) out.set(c.key, c);
    return out;
  }, [available]);

  // Active list is fed by the parent (canonical source for the table).
  // Inactive ordering is local UX state — users prepare the desired
  // order before checking columns on. Re-sync whenever the available
  // catalog or the active selection changes externally so newly added
  // unchecked keys end up at the bottom and removed keys disappear.
  const inactiveKeysFromProps = useMemo(() => {
    const activeSet = new Set(activeKeys);
    return available.filter((c) => !activeSet.has(c.key)).map((c) => c.key);
  }, [available, activeKeys]);

  const [inactiveOrder, setInactiveOrder] = useState<string[]>(
    inactiveKeysFromProps,
  );
  useEffect(() => {
    setInactiveOrder((prev) => {
      const allowed = new Set(inactiveKeysFromProps);
      const kept = prev.filter((k) => allowed.has(k));
      const seen = new Set(kept);
      for (const k of inactiveKeysFromProps) {
        if (!seen.has(k)) kept.push(k);
      }
      return kept;
    });
  }, [inactiveKeysFromProps]);

  const activeColumns = useMemo<AvailableColumn[]>(
    () =>
      activeKeys
        .map((k) => byKey.get(k))
        .filter((c): c is AvailableColumn => Boolean(c)),
    [activeKeys, byKey],
  );

  const inactiveColumns = useMemo<AvailableColumn[]>(
    () =>
      inactiveOrder
        .map((k) => byKey.get(k))
        .filter((c): c is AvailableColumn => Boolean(c)),
    [inactiveOrder, byKey],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleToggle(key: string, checked: boolean) {
    const col = byKey.get(key);
    if (!col || col.locked) return;
    if (checked) {
      // Inactive → active: append to the end of the active list. The
      // user can drag-reorder afterward.
      onChange([...activeKeys, key]);
    } else {
      // Active → inactive: prepend to the inactive list so the
      // just-hidden column is easy to spot and re-toggle.
      setInactiveOrder((prev) => [key, ...prev.filter((k) => k !== key)]);
      onChange(activeKeys.filter((k) => k !== key));
    }
  }

  function handleActiveDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = activeKeys.indexOf(String(active.id));
    const newIndex = activeKeys.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    onChange(arrayMove(activeKeys, oldIndex, newIndex));
  }

  function handleInactiveDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setInactiveOrder((prev) => {
      const oldIndex = prev.indexOf(String(active.id));
      const newIndex = prev.indexOf(String(over.id));
      if (oldIndex < 0 || newIndex < 0) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          className="text-slate-700"
        >
          <SlidersHorizontal className="mr-2 h-3.5 w-3.5" />
          {triggerLabel}
          {isCustomized && (
            <span className="ml-1.5 inline-block h-2 w-2 rounded-full bg-indigo-500" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[320px] p-0">
        <div className="flex items-center justify-between border-b border-slate-900/[0.06] px-4 py-2.5">
          <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
            Columns
          </div>
          <button
            type="button"
            onClick={onReset}
            className="text-xs font-medium text-indigo-600 hover:text-indigo-500"
          >
            Reset
          </button>
        </div>

        <div className="max-h-[440px] overflow-y-auto p-1">
          {/* Active columns — drag-reorderable. */}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleActiveDragEnd}
          >
            <SortableContext
              items={activeKeys}
              strategy={verticalListSortingStrategy}
            >
              {activeColumns.map((col) => (
                <SortableRow
                  key={col.key}
                  column={col}
                  checked
                  onToggle={(c) => handleToggle(col.key, c)}
                />
              ))}
            </SortableContext>
          </DndContext>

          {inactiveColumns.length > 0 && (
            <>
              {activeColumns.length > 0 && (
                <div className="my-1 border-t border-dashed border-slate-200" />
              )}
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleInactiveDragEnd}
              >
                <SortableContext
                  items={inactiveOrder}
                  strategy={verticalListSortingStrategy}
                >
                  {inactiveColumns.map((col) => (
                    <SortableRow
                      key={col.key}
                      column={col}
                      checked={false}
                      onToggle={(c) => handleToggle(col.key, c)}
                      muted
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </>
          )}

          {available.length === 0 && (
            <div className="px-3 py-4 text-center text-xs text-slate-400">
              No columns available.
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

interface RowProps {
  column: AvailableColumn;
  checked: boolean;
  onToggle: (checked: boolean) => void;
  muted?: boolean;
}

function SortableRow({ column, checked, onToggle, muted = false }: RowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.key });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    // The dragged row gets a slight lift so it visually separates from
    // its row-set during drag.
    zIndex: isDragging ? 10 : "auto",
  } as React.CSSProperties;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex items-center gap-2 rounded-md px-2 py-1.5 transition-shadow",
        isDragging
          ? "scale-[1.02] bg-white shadow-card-open ring-1 ring-indigo-300/40"
          : "hover:bg-slate-50",
      )}
    >
      <Checkbox
        checked={checked}
        disabled={column.locked}
        onCheckedChange={(v) => onToggle(v === true)}
        aria-label={`Toggle ${column.label}`}
      />
      <span
        className={cn(
          "flex-1 truncate text-sm",
          muted ? "text-slate-500" : "text-slate-700",
        )}
      >
        {column.label}
      </span>
      {column.locked && (
        <Lock className="h-3 w-3 shrink-0 text-slate-400" aria-label="Locked" />
      )}
      <button
        type="button"
        aria-label={`Reorder ${column.label}`}
        className="shrink-0 cursor-grab rounded p-1 text-slate-400 hover:text-slate-600 active:cursor-grabbing"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>
    </div>
  );
}
